import React, { useMemo } from "react";
import "./CardComponent.scss";

const formatTime = (timeValue) => {
  if (!timeValue) return "";

  try {
    const [hours, minutes] = String(timeValue).split(":");
    const h = Number(hours);
    const m = Number(minutes);

    if (Number.isNaN(h) || Number.isNaN(m)) return timeValue;

    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    const minuteText = String(m).padStart(2, "0");

    return `${hour12}.${minuteText} ${suffix}`;
  } catch {
    return timeValue;
  }
};

const formatDateParts = (rawDate) => {
  if (!rawDate) {
    return {
      weekday: "Saturday",
      fullDate: "18th April 2026",
    };
  }

  const parsed = new Date(rawDate);

  if (Number.isNaN(parsed.getTime())) {
    return {
      weekday: "",
      fullDate: rawDate,
    };
  }

  const weekday = parsed.toLocaleDateString("en-GB", { weekday: "long" });
  const day = parsed.getDate();

  const getOrdinal = (n) => {
    if (n > 3 && n < 21) return `${n}th`;
    switch (n % 10) {
      case 1:
        return `${n}st`;
      case 2:
        return `${n}nd`;
      case 3:
        return `${n}rd`;
      default:
        return `${n}th`;
    }
  };

  const monthYear = parsed.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  return {
    weekday,
    fullDate: `${getOrdinal(day)} ${monthYear}`,
  };
};

const CardComponent = ({
  title = "Reception",
  date,
  timeLine,
  venueName,
  venueAddress,
  mapsQuery,
  showDirections = true,
  weddingData,
}) => {
  const wedding_date = weddingData?.wedding_date || "";
  const wedding_location = weddingData?.wedding_location || "Wedding Venue";
  const poruwa_time = weddingData?.poruwa_time || "";
  const ceremony_time = weddingData?.ceremony_time || "";
  const hotel_address = weddingData?.hotel_address || "Venue Address";
  const ceremony_type = weddingData?.ceremony_type || "wedding";

  const resolvedDate = date || wedding_date;
  const resolvedVenueName = venueName || wedding_location;
  const resolvedVenueAddress = venueAddress || hotel_address;

  const detailsHeading =
    ceremony_type === "home coming" ? "Home Coming Details" : "Wedding Details";

  const resolvedTime = useMemo(() => {
    if (timeLine) return timeLine;

    const formattedCeremony = formatTime(ceremony_time);
    const formattedPoruwa = formatTime(poruwa_time);

    if (formattedCeremony) return `${formattedCeremony} Onwards`;
    if (formattedPoruwa) return `${formattedPoruwa} Onwards`;

    return "Time to be announced";
  }, [timeLine, ceremony_time, poruwa_time]);

  const { weekday, fullDate } = formatDateParts(resolvedDate);

  const q = encodeURIComponent(
    mapsQuery || `${resolvedVenueName}, ${resolvedVenueAddress}`
  );
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${q}`;

  return (
    <section className="winfoCard" aria-label={detailsHeading}>
      <div className="winfoCard__inner">
        <header className="winfoCard__top">
          <p className="winfoCard__section">
            {ceremony_type === "home coming" ? "Homecoming" : "Reception"}
          </p>
          <h2 className="winfoCard__headline">{detailsHeading}</h2>
        </header>

        <div className="winfoCard__main">
          <div className="winfoCard__block winfoCard__block--date">
            {weekday && <p className="winfoCard__eyebrow">{weekday}</p>}
            <p className="winfoCard__date">{fullDate}</p>
          </div>

          <div className="winfoCard__separator" />

          <div className="winfoCard__block winfoCard__block--time">
            <p className="winfoCard__eyebrow">Time</p>
            <p className="winfoCard__time">{resolvedTime}</p>
          </div>

          <div className="winfoCard__separator" />

          <div className="winfoCard__block winfoCard__block--venue">
            <p className="winfoCard__eyebrow">Venue</p>

            <div className="winfoCard__venueRow">
              <span className="winfoCard__pin" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                >
                  <path d="M12 22s7-5.1 7-12a7 7 0 1 0-14 0c0 6.9 7 12 7 12z" />
                  <circle cx="12" cy="10" r="2.5" />
                </svg>
              </span>

              <div className="winfoCard__venueText">
                <h3 className="winfoCard__venueName">{resolvedVenueName}</h3>
                <p className="winfoCard__venueAddress">{resolvedVenueAddress}</p>
              </div>
            </div>
          </div>
        </div>

        {showDirections && (
          <div className="winfoCard__actions">
            <a
              className="winfoCard__link"
              href={mapsLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Open directions in Google Maps"
            >
              View Location
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default CardComponent;